import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadSimple, Storefront } from "@phosphor-icons/react";
import { card, inputCls, btnPrimary, btnGhost, usd, errMsg, uploadToCloudinary } from "./shared";
import { MediaStrip } from "./BusinessCard";
import { SpendEntryForm } from "./SpendEntryForm";
import { PromotePanel } from "./PromotePanel";
import { IncomingRequests } from "./IncomingRequests";

const STATUS = {
  pending: ["Awaiting admin approval", "bg-amber-100 text-amber-700"],
  approved: ["Live", "bg-emerald-100 text-emerald-700"],
  rejected: ["Not approved", "bg-red-100 text-red-700"],
};

function ProfileEditor({ biz, reload, categories }) {
  const [cat, setCat] = useState(biz.category || "");
  const [wa, setWa] = useState(biz.whatsapp || "");
  const [desc, setDesc] = useState(biz.description || "");
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const save = async () => {
    try { await api.put("/market/business", { whatsapp: wa, description: desc, ...(cat ? { category: cat } : {}) }); toast.success("Saved"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setProgress(0);
    try { const m = await uploadToCloudinary(file, setProgress); await api.post("/market/business/media", m); reload(); }
    catch (e) { toast.error(errMsg(e, e.message)); }
    finally { setProgress(null); }
  };
  const del = async (id) => { await api.delete(`/market/business/media/${id}`); reload(); };

  return (
    <div className={`${card} p-5 space-y-3`} data-testid="business-profile-editor">
      <label className="block text-xs font-bold uppercase tracking-widest text-sky-600" htmlFor="biz-category">Category</label>
      <select id="biz-category" data-testid="business-category-input" className={inputCls} value={cat} onChange={(e) => setCat(e.target.value)}>
        <option value="">Choose a category</option>
        {categories.map((c) => <option key={c}>{c}</option>)}
      </select>
      <input data-testid="business-whatsapp-input" className={inputCls} placeholder="WhatsApp number with country code (e.g. 233501234567)" value={wa} onChange={(e) => setWa(e.target.value)} />
      <textarea data-testid="business-description-input" className={`${inputCls} h-24 py-2`} placeholder="What do you sell or offer?" value={desc} onChange={(e) => setDesc(e.target.value)} />
      <div className="flex flex-wrap gap-2">
        <button data-testid="business-save-btn" className={btnPrimary} onClick={save}>Save details</button>
        <button data-testid="business-upload-btn" className={btnGhost} onClick={() => fileRef.current?.click()} disabled={progress !== null}>
          <UploadSimple size={16} weight="bold" /> {progress !== null ? `Uploading ${progress}%` : "Add photo / video"}
        </button>
        <input ref={fileRef} type="file" accept="image/*,video/*" hidden data-testid="business-media-input" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
      <MediaStrip media={biz.media} onDelete={del} />
    </div>
  );
}

export function MyBusinessPanel({ biz, onRegister, reload, categories = [], spendFee = 0, promo }) {
  const [requests, setRequests] = useState([]);
  const [entries, setEntries] = useState([]);
  const live = biz && biz.status === "approved" && !biz.deleted;

  const loadExtras = useCallback(() => {
    if (!live) return;
    api.get("/market/requests/incoming").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/market/business/spend").then(({ data }) => setEntries(data)).catch(() => {});
  }, [live]);
  useEffect(() => { loadExtras(); }, [loadExtras]);

  if (!biz) {
    return (
      <div className={`${card} p-8 text-center space-y-4`} data-testid="no-business">
        <Storefront size={48} weight="duotone" className="mx-auto text-sky-500" />
        <p className="text-sky-900">You haven't registered a business yet.</p>
        <button data-testid="register-business-btn-empty" className={btnPrimary} onClick={onRegister}>REGISTER AS A BUSINESS</button>
      </div>
    );
  }
  const [label, cls] = biz.deleted ? ["Removed by admin", "bg-red-100 text-red-700"] : STATUS[biz.status];

  return (
    <div className="space-y-5">
      <div className={`${card} p-5 flex items-center justify-between gap-3`}>
        <div>
          <h3 className="font-heading text-2xl font-black text-sky-950" data-testid="my-business-name">{biz.name}</h3>
          <p className="text-sm text-sky-700">{biz.category ? `${biz.category} · ` : ""}{biz.location} · {biz.gender} · {biz.age}</p>
          {biz.rating_count > 0 && <p className="text-sm font-bold text-amber-500 mt-0.5" data-testid="my-business-rating">★ {biz.rating_avg.toFixed(1)} ({biz.rating_count})</p>}
        </div>
        <span data-testid="my-business-status" className={`text-xs font-bold px-3 py-1 rounded-full ${cls}`}>{label}</span>
      </div>
      {biz.status === "rejected" && !biz.deleted && (
        <button data-testid="register-business-resubmit" className={btnGhost} onClick={onRegister}>Resubmit registration</button>
      )}
      {!biz.deleted && <ProfileEditor biz={biz} reload={reload} categories={categories} />}
      {live && promo && <PromotePanel promo={promo} />}
      {live && (
        <div className="grid lg:grid-cols-2 gap-5">
          <IncomingRequests requests={requests} reload={loadExtras} />
          <div className="space-y-5">
            <SpendEntryForm onSaved={loadExtras} fee={spendFee} />
            <div className={`${card} p-5`} data-testid="spend-history">
              <p className="font-heading font-black text-sky-950 mb-2">Recent entries</p>
              {entries.length === 0 && <p className="text-sm text-sky-700/70">None yet.</p>}
              {entries.slice(0, 15).map((e) => (
                <div key={e.entry_id} className="flex justify-between text-sm py-1.5 border-b border-sky-50 last:border-0">
                  <span className="text-sky-900">{e.customer_name}</span>
                  <span className={`font-bold ${e.amount < 0 ? "text-red-500" : "text-emerald-600"}`}>{e.amount < 0 ? "-" : "+"}{usd(Math.abs(e.amount))}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
